import { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Layers, Image as ImageIcon, Search, AlertTriangle, Package, Pencil, Check, X, Move, LogOut, Plus, Loader2, Trash2, Sparkles, Wand2, Award, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { API, api, formatCOP, formatNumber } from "@/lib/api";
import { toast } from "sonner";
import { ColorSwatch } from "@/lib/colors";
import ImageLightbox from "@/components/ImageLightbox";

/**
 * Modal de detalle de una familia (agrupación por estilo).
 * Muestra todos los colores/variantes con foto, stock, precio y acciones rápidas.
 *
 * Props:
 *   - familia: { familiaKey, familiaNombre, variantes }
 *   - onClose
 *   - onAjustar(variante) → abre modal de ajuste
 *   - onEditar(variante) → abre modal edición
 *   - canAdjust, canEdit → permisos
 *   - isDirector → si es true, muestra los controles de edición de familia
 *   - onFamiliaCambio → callback tras renombrar/mover/sacar (para refrescar lista)
 */
export default function FamilyDetailModal({ familia, onClose, onAjustar, onEditar, canAdjust, canEdit, canArchive = false, isDirector = false, canBulk = false, reglas = [], onFamiliaCambio, onBulkFromFamily, onAplicarReglaFromFamily }) {
  const [busca, setBusca] = useState("");
  const [orden, setOrden] = useState("color"); // color | stock | precio
  const [lightbox, setLightbox] = useState(null); // { src, alt } | null

  // ---- Edición de familia (mar/2026) ----
  const [editandoNombre, setEditandoNombre] = useState(false);
  const [nombreLocal, setNombreLocal] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [moverAbiertoPara, setMoverAbiertoPara] = useState(null); // variante_id | "MULTI" | "MERGE_FAMILIA"
  const [seleccionMulti, setSeleccionMulti] = useState(new Set()); // ids seleccionados para mover en batch
  // Feb/2026: regla de comisión seleccionada en la barra bulk interna del modal
  const [bulkFamReglaId, setBulkFamReglaId] = useState("");
  const [familiasCatalogo, setFamiliasCatalogo] = useState([]);
  const [nombreGrupoNuevo, setNombreGrupoNuevo] = useState("");
  const [buscarFamilia, setBuscarFamilia] = useState(""); // filtro del dropdown de familias
  // Modal de eliminación con motivo (feb/2026) — permite archivar UN color
  // individual del grupo registrando motivo + ajuste de stock.
  const [eliminarVarianteTarget, setEliminarVarianteTarget] = useState(null); // {v, color}
  const [eliminarMotivo, setEliminarMotivo] = useState("");
  const [eliminarStockFinal, setEliminarStockFinal] = useState("0");
  const [eliminando, setEliminando] = useState(false);
  // Merge: al unir grupos, elegir qué nombre queda (destino o origen)
  const [mergeNombreElegido, setMergeNombreElegido] = useState("target"); // "target" | "source"
  // Feb/2026: edición inline de la "etiqueta" (color, tamaño, forma, etc.)
  // por variante. Permite personalizar el diferenciador sin abrir el modal
  // pesado de edición de producto.
  const [labelEditando, setLabelEditando] = useState(null); // v.id
  const [labelValor, setLabelValor] = useState("");
  const [labelGuardando, setLabelGuardando] = useState(false);
  const [labelMergeTarget, setLabelMergeTarget] = useState(null); // { source, target, nuevoLabel }
  // Feb/2026: fusión manual de duplicados detectados automáticamente.
  // El usuario elige cuál es "principal" (master) y el otro se fusiona ahí.
  // Lotes se re-asignan al master manteniendo fecha_recepcion para FIFO.
  const [fusionDupTarget, setFusionDupTarget] = useState(null); // { grupo: [variantes...], masterId }
  const [fusionGuardando, setFusionGuardando] = useState(false);
  // Feb/2026: fingerprints de duplicados que el usuario marcó "no fusionar".
  // Se recarga al abrir el modal. Cada fingerprint es
  // ids_ordenados.join("|"); si llega una variante nueva al mismo color, el
  // fingerprint cambia y el banner vuelve a aparecer.
  const [fingerprintsOmitidos, setFingerprintsOmitidos] = useState(new Set());
  // Oct/2026: banner de duplicados — colapsado por default (solo header +
  // badge con el conteo). El usuario toca "Ver" para expandir los chips de
  // fusión. Reduce de 3 filas a 1 el espacio que ocupa.
  const [bannerDupExpandido, setBannerDupExpandido] = useState(false);

  const abrirEdicionLabel = (v, colorActual) => {
    setLabelEditando(v.id);
    setLabelValor(colorActual === "—" ? "" : colorActual);
  };

  const guardarLabel = async (v) => {
    const nuevo = (labelValor || "").trim();
    if (!nuevo) {
      setLabelEditando(null);
      return;
    }
    // Detectar duplicado dentro del mismo grupo (case-insensitive)
    const nuevoLc = nuevo.toLowerCase();
    const duplicado = familia?.variantes.find((otra) => {
      if (otra.id === v.id) return false;
      const otroColor = otra.color || String(otra.nombre || "").split(" - ")[1]?.trim() || "";
      return otroColor.toLowerCase() === nuevoLc;
    });
    if (duplicado) {
      // Ofrecer merge en vez de guardar duplicado
      setLabelMergeTarget({ source: v, target: duplicado, nuevoLabel: nuevo });
      return;
    }
    setLabelGuardando(true);
    try {
      // Oct/2026 bug fix: antes SIEMPRE reconstruíamos el nombre como
      // "${familiaNombre} - ${nuevo}". Esto sobrescribía nombres custom
      // que el usuario había tipeado manualmente (ej. "GUIPIUR 4.5cm Blanco
      // Perlado Especial"). Ahora SOLO reescribimos el nombre si el nombre
      // actual sigue EXACTAMENTE el patrón "familia - color_viejo" (es decir,
      // nunca fue customizado). Si no sigue el patrón, respetamos el nombre
      // del usuario y SOLO actualizamos el campo `color`.
      const familiaNombre = (familia?.familiaNombre || "").trim();
      const colorViejo = (v.color || "").trim();
      const nombreActual = (v.nombre || "").trim();
      const patronAuto = familiaNombre && colorViejo ? `${familiaNombre} - ${colorViejo}` : "";
      const esNombreAutomatico = patronAuto && nombreActual.toLowerCase() === patronAuto.toLowerCase();

      const payload = {
        color: nuevo,
        motivo: "Actualización de etiqueta desde modal familiar",
      };
      if (esNombreAutomatico) {
        payload.nombre = `${familiaNombre} - ${nuevo}`;
      }
      // else: preservamos el nombre custom del usuario tal como lo tipeó
      await api.patch(`/productos/${v.id}`, payload);
      toast.success(
        esNombreAutomatico
          ? `Etiqueta actualizada: ${nuevo}`
          : `Color actualizado a "${nuevo}" (nombre personalizado preservado)`
      );
      setLabelEditando(null);
      setLabelValor("");
      onFamiliaCambio?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setLabelGuardando(false);
    }
  };

  const confirmarMerge = async () => {
    if (!labelMergeTarget) return;
    setLabelGuardando(true);
    try {
      const { data } = await api.post("/productos/variantes/merge", {
        source_id: labelMergeTarget.source.id,
        target_id: labelMergeTarget.target.id,
      });
      toast.success(
        `Fusionado: ${data.stock_sumado} unidades sumadas a "${labelMergeTarget.nuevoLabel}" (total: ${data.nuevo_stock_target})`
      );
      setLabelMergeTarget(null);
      setLabelEditando(null);
      setLabelValor("");
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo fusionar");
    } finally {
      setLabelGuardando(false);
    }
  };

  useEffect(() => {
    if (familia) setNombreLocal(familia.familiaNombre || "");
    setEditandoNombre(false);
    setMoverAbiertoPara(null);
    setSeleccionMulti(new Set());
    setBuscarFamilia("");
  }, [familia?.familiaKey]);

  // Cargar fingerprints de duplicados ya "omitidos" por el usuario para esta
  // familia. Se usa en `duplicadosDetectados` para no re-sugerir los que el
  // usuario explícitamente decidió mantener separados.
  useEffect(() => {
    if (!familia?.familiaKey) { setFingerprintsOmitidos(new Set()); return; }
    api.get("/productos/fusiones/omitidas", { params: { familia_key: familia.familiaKey } })
      .then(({ data }) => {
        const fps = (data?.omitidas || []).map((o) => o.fingerprint).filter(Boolean);
        setFingerprintsOmitidos(new Set(fps));
      })
      .catch(() => setFingerprintsOmitidos(new Set()));
  }, [familia?.familiaKey]);

  // Cargar familias disponibles del catálogo para el selector "Mover a"
  useEffect(() => {
    if (!familia || !isDirector) return;
    api.get("/productos/familias/buscar", { params: { q: "", limit: 50 } })
      .then(({ data }) => setFamiliasCatalogo(Array.isArray(data) ? data : []))
      .catch(() => setFamiliasCatalogo([]));
  }, [familia?.familiaKey, isDirector]);

  const guardarNombre = async () => {
    const val = (nombreLocal || "").trim();
    if (!val || val === familia.familiaNombre) { setEditandoNombre(false); return; }
    setGuardando(true);
    try {
      const nuevaKey = val.toUpperCase();
      await api.post("/productos/familias/renombrar", {
        familia_key_actual: familia.familiaKey,
        nueva_familia_key: nuevaKey,
        actualizar_nombres: true,
      });
      toast.success(`Familia renombrada a "${val}"`);
      setEditandoNombre(false);
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo renombrar");
    } finally {
      setGuardando(false);
    }
  };

  const moverVarianteA = async (varianteId, targetKey, targetNombre) => {
    setGuardando(true);
    try {
      await api.post("/productos/familias/mover", {
        producto_ids: [varianteId],
        target_familia_key: targetKey,
        target_nombre: targetNombre || null,
        actualizar_nombres: !!targetNombre,
      });
      toast.success(`Movido a "${targetNombre || targetKey}"`);
      setMoverAbiertoPara(null);
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo mover");
    } finally {
      setGuardando(false);
    }
  };

  const moverMultiplesA = async (targetKey, targetNombre) => {
    if (!seleccionMulti.size) { toast.error("Selecciona al menos un ítem"); return; }
    setGuardando(true);
    try {
      const { data } = await api.post("/productos/familias/mover", {
        producto_ids: Array.from(seleccionMulti),
        target_familia_key: targetKey,
        target_nombre: targetNombre || null,
        actualizar_nombres: !!targetNombre,
      });
      toast.success(`${data.actualizados} ítems movidos a "${targetNombre || targetKey}"`);
      setSeleccionMulti(new Set());
      setMoverAbiertoPara(null);
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron mover");
    } finally {
      setGuardando(false);
    }
  };

  const mergeFamiliaCompleta = async (targetKey, targetNombreDestino) => {
    // Mover TODAS las variantes de esta familia a la familia destino, con el
    // nombre elegido por el usuario (destino o origen).
    if (!familia) return;
    const varianteIds = familia.variantes.map((v) => v.id);
    const nombreFinal = mergeNombreElegido === "source" ? familia.familiaNombre : targetNombreDestino;
    setGuardando(true);
    try {
      // 1. Si el usuario eligió el nombre origen: renombrar la familia destino primero
      //    (esto renombra los productos existentes de la familia destino).
      if (mergeNombreElegido === "source" && nombreFinal && nombreFinal !== targetNombreDestino) {
        await api.post("/productos/familias/renombrar", {
          familia_key_actual: targetKey,
          nombre_nuevo: nombreFinal,
        });
      }
      // 2. Mover todas las variantes de esta familia al destino con el nombre final
      const { data } = await api.post("/productos/familias/mover", {
        producto_ids: varianteIds,
        target_familia_key: (nombreFinal || targetKey).toUpperCase(),
        target_nombre: nombreFinal,
        actualizar_nombres: true,
      });
      toast.success(`Grupos unidos: ${data.actualizados} ítems ahora en "${nombreFinal}"`);
      setMoverAbiertoPara(null);
      setSeleccionMulti(new Set());
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudieron unir los grupos");
    } finally {
      setGuardando(false);
    }
  };

  const toggleSeleccionMulti = (id) => {
    setSeleccionMulti((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const crearGrupoConVariante = async (varianteId) => {
    const nombre = (nombreGrupoNuevo || "").trim();
    if (!nombre) { toast.error("Escribe el nombre del nuevo grupo"); return; }
    await moverVarianteA(varianteId, nombre.toUpperCase(), nombre);
    setNombreGrupoNuevo("");
  };

  const sacarVariante = async (varianteId, colorLabel) => {
    if (!confirm(`¿Sacar "${colorLabel}" de esta familia? Quedará como producto individual.`)) return;
    setGuardando(true);
    try {
      await api.post("/productos/familias/sacar", { producto_id: varianteId });
      toast.success(`"${colorLabel}" sacado de la familia`);
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sacar");
    } finally {
      setGuardando(false);
    }
  };

  const abrirEliminarVariante = (v, color) => {
    setEliminarVarianteTarget({ v, color });
    setEliminarMotivo("");
    setEliminarStockFinal("0");
  };

  const confirmarEliminarVariante = async () => {
    if (!eliminarVarianteTarget) return;
    const motivo = (eliminarMotivo || "").trim();
    if (!motivo) {
      toast.error("El motivo es obligatorio");
      return;
    }
    setEliminando(true);
    try {
      await api.post(`/productos/${eliminarVarianteTarget.v.id}/eliminar-con-motivo`, {
        motivo,
        stock_final: Number(eliminarStockFinal || 0),
      });
      toast.success(`"${eliminarVarianteTarget.color}" eliminado del grupo`);
      setEliminarVarianteTarget(null);
      setEliminarMotivo("");
      setEliminarStockFinal("0");
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setEliminando(false);
    }
  };

  const stats = useMemo(() => {
    if (!familia) return null;
    const v = familia.variantes;
    const stockTotal = v.reduce((s, x) => s + ((x.stock_actual || 0) - (x.stock_reservado || 0)), 0);
    const precios = v.map((x) => x.precio_sugerido || x.precio_base || 0).filter((p) => p > 0);
    const precioMin = precios.length ? Math.min(...precios) : 0;
    const precioMax = precios.length ? Math.max(...precios) : 0;
    const lowCount = v.filter((x) => ((x.stock_actual || 0) - (x.stock_reservado || 0)) <= (x.stock_minimo || 0)).length;
    const sinStock = v.filter((x) => ((x.stock_actual || 0) - (x.stock_reservado || 0)) <= 0).length;
    const sinFoto = v.filter((x) => !x.imagen_path).length;
    return { stockTotal, precioMin, precioMax, lowCount, sinStock, sinFoto };
  }, [familia]);

  // Feb/2026: detectar duplicados por color/etiqueta dentro de la familia.
  // Devuelve un array de grupos; cada grupo es un array de variantes con el
  // mismo color normalizado (sin acentos, case-insensitive, trim). Usado para
  // mostrar el banner "⚠ Duplicados detectados".
  //
  // Oct/2026: filtra grupos cuyo fingerprint (sorted ids joined by "|") ya
  // está en `fingerprintsOmitidos` (el usuario dijo "no fusionar"). Si llega
  // una variante nueva al mismo color, el fingerprint cambia y el banner
  // reaparece automáticamente.
  const duplicadosDetectados = useMemo(() => {
    if (!familia) return [];
    const norm = (s) => (s || "")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, " ");
    const mapa = new Map();
    for (const v of familia.variantes) {
      const col = v.color || String(v.nombre || "").split(" - ")[1] || "";
      const key = norm(col);
      if (!key || key === "—") continue;
      if (!mapa.has(key)) mapa.set(key, []);
      mapa.get(key).push(v);
    }
    return Array.from(mapa.entries())
      .filter(([, arr]) => arr.length >= 2)
      .map(([key, arr]) => ({
        key,
        variantes: arr,
        fingerprint: [...arr.map((v) => v.id)].sort().join("|"),
      }))
      .filter((grp) => !fingerprintsOmitidos.has(grp.fingerprint));
  }, [familia, fingerprintsOmitidos]);

  // Oct/2026: marca un grupo de duplicados como "no fusionar". Guarda el
  // fingerprint (ids ordenados) en Mongo. Si en el futuro aparece una variante
  // nueva con el mismo color, el fingerprint cambia y el banner reaparece.
  const omitirFusion = async (grp) => {
    try {
      await api.post("/productos/fusiones/omitir", {
        familia_key: familia.familiaKey,
        color_norm: grp.key,
        variante_ids: grp.variantes.map((v) => v.id),
      });
      setFingerprintsOmitidos((prev) => new Set([...prev, grp.fingerprint]));
      const label = grp.variantes[0].color || grp.variantes[0].nombre?.split(" - ")[1] || grp.key;
      toast.success(`Entendido: "${label}" quedan como referencias distintas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la preferencia");
    }
  };

  const fusionarDuplicados = async () => {
    if (!fusionDupTarget || !fusionDupTarget.masterId) {
      toast.error("Elige la variante principal");
      return;
    }    const master = fusionDupTarget.grupo.find((v) => v.id === fusionDupTarget.masterId);
    const sources = fusionDupTarget.grupo.filter((v) => v.id !== fusionDupTarget.masterId);
    if (!master || sources.length === 0) return;
    setFusionGuardando(true);
    try {
      let totalSumado = 0;
      let totalLotes = 0;
      let totalPedidos = 0;
      for (const src of sources) {
        const { data } = await api.post("/productos/variantes/merge", {
          source_id: src.id,
          target_id: master.id,
        });
        totalSumado += data.stock_sumado || 0;
        totalLotes += data.lotes_movidos || 0;
        totalPedidos += data.pedidos_activos_reasignados || 0;
      }
      const resumen = [
        `${sources.length} variante${sources.length === 1 ? "" : "s"} fusionada${sources.length === 1 ? "" : "s"} en "${master.color || master.nombre}"`,
        totalLotes ? `${totalLotes} lote${totalLotes === 1 ? "" : "s"} movido${totalLotes === 1 ? "" : "s"}` : null,
        totalPedidos ? `${totalPedidos} pedido${totalPedidos === 1 ? "" : "s"} reasignado${totalPedidos === 1 ? "" : "s"}` : null,
        totalSumado ? `${totalSumado} unidades sumadas` : null,
      ].filter(Boolean).join(" · ");
      toast.success(resumen);
      setFusionDupTarget(null);
      onFamiliaCambio?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo fusionar");
    } finally {
      setFusionGuardando(false);
    }
  };

  const variantesFiltradas = useMemo(() => {
    if (!familia) return [];
    const t = busca.trim().toLowerCase();
    let arr = familia.variantes;
    if (t) {
      arr = arr.filter((v) =>
        (v.color || "").toLowerCase().includes(t) ||
        (v.sku || "").toLowerCase().includes(t) ||
        (v.codigo || "").toLowerCase().includes(t) ||
        (v.nombre || "").toLowerCase().includes(t) ||
        (v.descripcion || "").toLowerCase().includes(t)
      );
    }
    const copy = [...arr];
    if (orden === "stock") {
      copy.sort((a, b) => ((b.stock_actual || 0) - (b.stock_reservado || 0)) - ((a.stock_actual || 0) - (a.stock_reservado || 0)));
    } else if (orden === "precio") {
      copy.sort((a, b) => (b.precio_sugerido || 0) - (a.precio_sugerido || 0));
    } else {
      copy.sort((a, b) => (a.color || "").localeCompare(b.color || ""));
    }
    return copy;
  }, [familia, busca, orden]);

  if (!familia) return null;

  return (
    <Dialog open={!!familia} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[94vh] overflow-hidden flex flex-col gap-0" data-testid="familia-detail-modal">
        <DialogHeader className="pb-1 flex-shrink-0">
          <DialogTitle className="font-display text-lg flex items-center gap-2 flex-wrap">
            <Layers className="w-5 h-5 text-primary flex-shrink-0" />
            {editandoNombre ? (
              <div className="flex-1 flex items-center gap-1">
                <Input
                  value={nombreLocal}
                  onChange={(e) => setNombreLocal(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); guardarNombre(); }
                    if (e.key === "Escape") { setEditandoNombre(false); setNombreLocal(familia.familiaNombre); }
                  }}
                  autoFocus
                  className="h-8 text-base font-display"
                  disabled={guardando}
                  data-testid="fam-nombre-input"
                />
                <Button size="sm" onClick={guardarNombre} disabled={guardando} className="h-8" data-testid="fam-nombre-guardar">
                  {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setEditandoNombre(false); setNombreLocal(familia.familiaNombre); }} className="h-8" data-testid="fam-nombre-cancelar">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <span className="truncate">{familia.familiaNombre}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground uppercase font-semibold">
                  {familia.variantes.length} {familia.variantes.length === 1 ? "color" : "colores"}
                </span>
                {stats && (
                  <>
                    <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                      {formatNumber(stats.stockTotal)} stock
                    </span>
                    <span className="text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                      {stats.precioMin === stats.precioMax ? formatCOP(stats.precioMin) : `${formatCOP(stats.precioMin)}—${formatCOP(stats.precioMax)}`}
                    </span>
                    {stats.lowCount > 0 && (
                      <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-2 py-0.5 rounded flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> {stats.lowCount} bajo
                      </span>
                    )}
                    {stats.sinStock > 0 && (
                      <span className="text-[11px] font-semibold text-rose-700 bg-rose-100 px-2 py-0.5 rounded">
                        {stats.sinStock} sin stock
                      </span>
                    )}
                  </>
                )}
                {isDirector && (
                  <button
                    onClick={() => setEditandoNombre(true)}
                    className="ml-1 p-1 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10"
                    title="Renombrar familia"
                    data-testid="fam-editar-nombre-btn"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </>
            )}
          </DialogTitle>
          <div className="text-[10px] font-mono text-muted-foreground/70">{familia.familiaKey}</div>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap mt-1 pb-2 border-b border-border flex-shrink-0">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
            <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar color, código real (EF26-XX), SKU..." className="pl-9 h-8 text-sm" data-testid="fam-detail-buscar" />
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-muted-foreground">Ordenar:</span>
            <button className={`px-2 py-1 rounded-md ${orden === "color" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} onClick={() => setOrden("color")} data-testid="fam-sort-color">Color</button>
            <button className={`px-2 py-1 rounded-md ${orden === "stock" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} onClick={() => setOrden("stock")} data-testid="fam-sort-stock">Stock</button>
            <button className={`px-2 py-1 rounded-md ${orden === "precio" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} onClick={() => setOrden("precio")} data-testid="fam-sort-precio">Precio</button>
          </div>
        </div>

        {/* Toolbar director/bulk: acciones masivas y merge.
            Feb/2026: ahora también muestra Precios/Código/Empaque/Regla cuando
            el usuario tiene permiso de bulk (admin + operador). Mover/Unir
            siguen siendo solo director. */}
        {(canBulk || isDirector) && (
          <div className="flex items-center gap-2 flex-wrap pt-2 pb-1 text-xs">
            {seleccionMulti.size > 0 ? (
              <>
                <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[11px] font-bold tabular-nums">{seleccionMulti.size}</span>
                <span className="text-primary font-semibold">
                  {seleccionMulti.size === 1 ? "ítem" : "ítems"} seleccionado{seleccionMulti.size === 1 ? "" : "s"}
                </span>
                <span className="w-px h-5 bg-border mx-0.5" />
                {/* --- Bulk ops delegados al parent (ProductosPage) --- */}
                {canBulk && onBulkFromFamily && (
                  <>
                    <button
                      onClick={() => onBulkFromFamily("precios", Array.from(seleccionMulti))}
                      className="h-7 px-2.5 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 text-[11px] font-semibold flex items-center gap-1 transition-colors"
                      data-testid="fam-detail-bulk-precios"
                      title="Precios masivos"
                    >
                      <Sparkles className="w-3 h-3" /> Precios
                    </button>
                    <button
                      onClick={() => onBulkFromFamily("codigo", Array.from(seleccionMulti))}
                      className="h-7 px-2.5 rounded-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-[11px] font-semibold flex items-center gap-1 transition-colors"
                      data-testid="fam-detail-bulk-codigo"
                      title="Código real masivo"
                    >
                      <Wand2 className="w-3 h-3" /> Código
                    </button>
                    <button
                      onClick={() => onBulkFromFamily("empaque", Array.from(seleccionMulti))}
                      className="h-7 px-2.5 rounded-full bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 text-[11px] font-semibold flex items-center gap-1 transition-colors"
                      data-testid="fam-detail-bulk-empaque"
                      title="Unidad de empaque masiva"
                    >
                      <Package className="w-3 h-3" /> Empaque
                    </button>
                  </>
                )}
                {/* --- Regla de comisión inline (si hay reglas disponibles) --- */}
                {canBulk && reglas.length > 0 && onAplicarReglaFromFamily && (
                  <>
                    <select
                      value={bulkFamReglaId}
                      onChange={(e) => setBulkFamReglaId(e.target.value)}
                      className="h-7 px-2 rounded-full bg-white border border-border text-[11px] font-medium focus:outline-none focus:ring-2 focus:ring-primary/40"
                      data-testid="fam-detail-bulk-regla-select"
                      title="Regla de comisión"
                    >
                      <option value="">Regla…</option>
                      {reglas.map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                    </select>
                    <button
                      disabled={!bulkFamReglaId}
                      onClick={async () => {
                        if (!bulkFamReglaId) return;
                        await onAplicarReglaFromFamily(bulkFamReglaId, Array.from(seleccionMulti));
                        setBulkFamReglaId("");
                        setSeleccionMulti(new Set());
                      }}
                      className="h-7 px-2.5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground text-[11px] font-semibold flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="fam-detail-bulk-regla-apply"
                      title="Aplicar regla"
                    >
                      <Award className="w-3 h-3" /> Regla
                    </button>
                  </>
                )}
                {/* --- Mover / Unir (solo director) --- */}
                {isDirector && (
                  <button
                    onClick={() => setMoverAbiertoPara("MULTI")}
                    disabled={guardando}
                    className="h-7 px-2.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 text-[11px] font-semibold flex items-center gap-1"
                    data-testid="fam-detail-mover-seleccionados"
                  >
                    <Move className="w-3 h-3" /> Mover
                  </button>
                )}
                <button
                  onClick={() => setSeleccionMulti(new Set())}
                  disabled={guardando}
                  className="h-7 w-7 rounded-full hover:bg-muted/70 flex items-center justify-center ml-auto"
                  data-testid="fam-detail-limpiar-seleccion"
                  title="Limpiar selección"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              familia.variantes.length > 1 && (
                <>
                  <button
                    onClick={() => setSeleccionMulti(new Set(familia.variantes.map((v) => v.id)))}
                    className="h-7 px-2.5 rounded-full bg-white border border-border text-foreground text-[11px] font-semibold hover:bg-muted/40"
                    data-testid="fam-detail-seleccionar-todos"
                  >
                    Seleccionar todos
                  </button>
                  {isDirector && (
                    <button
                      onClick={() => { setMergeNombreElegido("target"); setMoverAbiertoPara("MERGE_FAMILIA"); }}
                      disabled={guardando}
                      className="h-7 px-2.5 rounded-full bg-primary/5 border border-primary/40 text-primary text-[11px] font-semibold hover:bg-primary/10 flex items-center gap-1"
                      data-testid="fam-detail-unir-grupo"
                    >
                      <Layers className="w-3 h-3" /> Unir con otro grupo
                    </button>
                  )}
                </>
              )
            )}
          </div>
        )}

        {/* Banner de duplicados (Feb/2026, inline chips Oct/2026) — muestra
            los chips de fusión DIRECTAMENTE visibles pero compactos. El toggle
            expande/colapsa cuando hay más chips de los que caben visibles.
            flex-shrink-0 es CRÍTICO: sin él el banner se encoge a 11px cuando
            el grid tiene muchos productos (bug reportado Oct/2026). */}
        {isDirector && duplicadosDetectados.length > 0 && (
          <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 overflow-hidden flex-shrink-0" data-testid="fam-duplicados-banner">
            <div className="flex items-start gap-2 px-3 py-2">
              <AlertTriangle className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[11px] font-bold text-amber-900">
                    {duplicadosDetectados.length === 1
                      ? `1 color duplicado · ${duplicadosDetectados[0].variantes.length} var:`
                      : `${duplicadosDetectados.length} duplicados · ${duplicadosDetectados.reduce((s, g) => s + g.variantes.length, 0)} var:`}
                  </span>
                  {/* Chips de fusión — todos visibles si son pocos, o los primeros 3 + "ver más" */}
                  {(bannerDupExpandido ? duplicadosDetectados : duplicadosDetectados.slice(0, 3)).map((grp) => {
                    const label = grp.variantes[0].color || grp.variantes[0].nombre?.split(" - ")[1] || grp.key;
                    const master = [...grp.variantes].sort((a, b) => {
                      const ta = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
                      const tb = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
                      return ta - tb;
                    })[0];
                    return (
                      <div key={grp.key} className="inline-flex items-center gap-0.5 bg-white rounded-full border border-amber-300 pr-0.5 shadow-sm">
                        <button
                          onClick={() => setFusionDupTarget({ grupo: grp.variantes, masterId: master.id })}
                          className="h-6 pl-2 pr-1.5 rounded-l-full bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold flex items-center gap-1"
                          data-testid={`fam-fusionar-dup-${grp.key}`}
                          title={`Fusionar ${grp.variantes.length} variantes "${label}"`}
                        >
                          <Layers className="w-2.5 h-2.5" />
                          Fusionar "{label}" ({grp.variantes.length})
                        </button>
                        <button
                          onClick={() => omitirFusion(grp)}
                          className="h-5 w-5 rounded-full hover:bg-amber-100 text-amber-700 flex items-center justify-center"
                          data-testid={`fam-omitir-dup-${grp.key}`}
                          title={`No fusionar — mantener "${label}" como referencias distintas`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                  {/* Botón "ver +N más" si hay más de 3 chips */}
                  {duplicadosDetectados.length > 3 && (
                    <button
                      onClick={() => setBannerDupExpandido((v) => !v)}
                      className="h-6 px-2 rounded-full bg-amber-200 hover:bg-amber-300 text-amber-900 text-[10px] font-bold flex items-center gap-1"
                      data-testid="fam-duplicados-toggle"
                    >
                      {bannerDupExpandido ? "Ver menos" : `+${duplicadosDetectados.length - 3} más`}
                      <ChevronDown className={`w-3 h-3 transition-transform ${bannerDupExpandido ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                <div className="text-[10px] text-amber-800/70 mt-1">
                  Los lotes se mantienen separados con sus fechas (FIFO respeta antigüedad). La X oculta ese grupo para siempre.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Grid de variantes */}
        <div className="flex-1 overflow-y-auto -mx-2 px-2 mt-2">
          {variantesFiltradas.length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground">
              <Package className="w-8 h-8 mx-auto text-muted-foreground/50 mb-2" />
              {busca ? "Ningún color coincide con la búsqueda." : "No hay variantes en esta familia."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-2 pb-2">
              {variantesFiltradas.map((v) => {
                const disp = (v.stock_actual || 0) - (v.stock_reservado || 0);
                const low = disp <= (v.stock_minimo || 0);
                const outOfStock = disp <= 0;
                const color = v.color || v.nombre?.split(" - ")[1]?.trim() || "—";
                return (
                  <div key={v.id} className={`border rounded-lg overflow-hidden hover:shadow-md transition-all bg-card ${seleccionMulti.has(v.id) ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-primary/40"}`} data-testid={`fam-detail-variante-${v.id}`}>
                    <div className="aspect-square bg-muted/40 relative overflow-hidden">
                      {v.imagen_path ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setLightbox({ src: `${API}/catalogo/img/${v.id}?w=800&v=${v.imagen_actualizada_at || 0}`, alt: `${v.nombre || color}` }); }}
                          className="w-full h-full block cursor-zoom-in hover:opacity-90 transition-opacity"
                          title="Ver imagen en grande"
                          data-testid={`fam-detail-img-${v.id}`}
                        >
                          <img src={`${API}/catalogo/img/${v.id}?w=300&v=${v.imagen_actualizada_at || 0}`} alt={color} loading="lazy" className="w-full h-full object-cover" />
                        </button>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground/40">
                          <ImageIcon className="w-7 h-7" />
                        </div>
                      )}
                      {outOfStock && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-600 text-white uppercase font-bold tracking-wider">Sin stock</span>
                        </div>
                      )}
                      {(isDirector || canBulk) && (
                        <label className="absolute top-1 left-1 bg-white/90 rounded-md p-0.5 cursor-pointer shadow-sm border border-border" title="Seleccionar para acción masiva">
                          <input
                            type="checkbox"
                            checked={seleccionMulti.has(v.id)}
                            onChange={() => toggleSeleccionMulti(v.id)}
                            className="w-3 h-3 accent-primary cursor-pointer block"
                            data-testid={`fam-detail-select-${v.id}`}
                          />
                        </label>
                      )}
                    </div>
                    <div className="p-1.5 space-y-0.5">
                      <div className="flex items-center gap-1">
                        <ColorSwatch nombre={color} size={11} />
                        {labelEditando === v.id ? (
                          <input
                            type="text"
                            value={labelValor}
                            onChange={(e) => setLabelValor(e.target.value)}
                            onBlur={() => guardarLabel(v)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") { e.preventDefault(); guardarLabel(v); }
                              if (e.key === "Escape") { setLabelEditando(null); setLabelValor(""); }
                            }}
                            className="flex-1 text-xs font-semibold px-1 py-0 border border-primary rounded h-5 min-w-0"
                            autoFocus
                            disabled={labelGuardando}
                            placeholder="Ej: 2cm, Rosa Pastel..."
                            data-testid={`fam-detail-label-input-${v.id}`}
                          />
                        ) : (
                          <button
                            type="button"
                            onClick={() => canEdit && abrirEdicionLabel(v, color)}
                            className={`flex-1 min-w-0 text-left ${canEdit ? "cursor-pointer hover:bg-muted/60 rounded px-0.5" : "cursor-default"}`}
                            title={canEdit ? "Toca para editar la etiqueta (color, tamaño, forma…)" : color}
                            data-testid={`fam-detail-label-btn-${v.id}`}
                          >
                            <div className="text-xs font-semibold truncate">{color}</div>
                          </button>
                        )}
                      </div>
                      <div className="text-[9px] font-mono text-muted-foreground/80 truncate" title={v.codigo ? "Código real" : "Sin código real"}>{v.codigo || v.sku || "—"}</div>
                      {/* Precio y stock en una sola fila compacta debajo de la foto (SIN overlay) */}
                      <div className="flex items-baseline justify-between gap-1 pt-0.5">
                        <div className="text-xs font-bold text-foreground tabular-nums leading-none truncate">{formatCOP(v.precio_sugerido || v.precio_base || 0)}</div>
                        <div className={`text-xs font-bold tabular-nums leading-none ${low ? "text-rose-600" : "text-foreground"}`}>{formatNumber(disp)}<span className="text-[9px] font-normal text-muted-foreground/70 ml-0.5">disp</span></div>
                      </div>
                      {(canAdjust || canEdit || isDirector) && (
                        <div className="flex gap-0.5 pt-0.5 border-t border-border/60">
                          {canAdjust && (
                            <button onClick={() => onAjustar?.(v)} className="flex-1 text-[9px] py-0.5 rounded text-primary hover:bg-primary/10 font-semibold" data-testid={`fam-detail-ajustar-${v.id}`}>
                              Ajustar
                            </button>
                          )}
                          {canEdit && (
                            <button onClick={() => onEditar?.(v)} className="flex-1 text-[9px] py-0.5 rounded text-muted-foreground hover:bg-muted font-semibold" data-testid={`fam-detail-editar-${v.id}`}>
                              Editar
                            </button>
                          )}
                          {isDirector && (
                            <button onClick={() => setMoverAbiertoPara(v.id)} className="flex-1 text-[9px] py-0.5 rounded text-blue-700 hover:bg-blue-50 font-semibold flex items-center justify-center gap-0.5" data-testid={`fam-detail-mover-${v.id}`} title="Mover a otra familia">
                              <Move className="w-2.5 h-2.5" />
                            </button>
                          )}
                        </div>
                      )}
                      {isDirector && familia.variantes.length > 1 && (
                        <button
                          onClick={() => sacarVariante(v.id, color)}
                          className="w-full text-[9px] py-0 text-amber-800 hover:bg-amber-50 rounded font-semibold flex items-center justify-center gap-0.5"
                          data-testid={`fam-detail-sacar-${v.id}`}
                          title="Sacar de esta familia"
                        >
                          <LogOut className="w-2 h-2" /> Sacar
                        </button>
                      )}
                      {canArchive && (
                        <button
                          onClick={() => abrirEliminarVariante(v, color)}
                          className="w-full text-[9px] py-0 text-rose-700 hover:bg-rose-50 rounded font-semibold flex items-center justify-center gap-0.5"
                          data-testid={`fam-detail-eliminar-${v.id}`}
                          title="Eliminar sólo este color con motivo"
                        >
                          <Trash2 className="w-2 h-2" /> Eliminar
                        </button>
                      )}
                      {v.descripcion && (
                        <div
                          className="text-[9px] text-muted-foreground/80 leading-tight line-clamp-2"
                          title={v.descripcion}
                        >
                          {v.descripcion}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="pt-3 border-t border-border flex justify-end">
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </div>

        {/* Sub-modal: Mover a otra familia (soporta single / MULTI / MERGE_FAMILIA) */}
        {moverAbiertoPara && (() => {
          const modo = moverAbiertoPara === "MULTI" ? "multi" : moverAbiertoPara === "MERGE_FAMILIA" ? "merge" : "single";
          const cuantosItems = modo === "multi" ? seleccionMulti.size : modo === "merge" ? familia.variantes.length : 1;
          const titulo = modo === "merge"
            ? `Unir "${familia.familiaNombre}" con otro grupo`
            : modo === "multi"
              ? `Mover ${cuantosItems} ${cuantosItems === 1 ? "ítem" : "ítems"} seleccionados`
              : "Mover a otra familia";
          const familiasFiltradas = familiasCatalogo
            .filter((f) => f.familia_key !== familia.familiaKey)
            .filter((f) => {
              if (!buscarFamilia.trim()) return true;
              const q = buscarFamilia.trim().toLowerCase();
              return (f.nombre_representativo || "").toLowerCase().includes(q) || (f.familia_key || "").toLowerCase().includes(q);
            });
          const ejecutarMover = (targetKey, targetNombre) => {
            if (modo === "multi") return moverMultiplesA(targetKey, targetNombre);
            if (modo === "merge") return mergeFamiliaCompleta(targetKey, targetNombre);
            return moverVarianteA(moverAbiertoPara, targetKey, targetNombre);
          };
          return (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={() => setMoverAbiertoPara(null)}>
              <div
                className="bg-card rounded-lg shadow-xl max-w-lg w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                data-testid="mover-variante-modal"
              >
                <div className="flex items-center gap-2 border-b border-border pb-2">
                  <Move className="w-4 h-4 text-primary" />
                  <div className="font-semibold text-foreground">{titulo}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  {modo === "merge"
                    ? "Elige la familia destino. Ambos grupos se unirán en uno solo — luego escogerás qué nombre queda."
                    : `Elige la familia destino o crea una nueva. Se renombrarán ${cuantosItems === 1 ? "el ítem" : "los ítems"} para reflejarlo.`}
                </div>

                {/* Selector de nombre final (solo modo merge) */}
                {modo === "merge" && (
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-2 space-y-1">
                    <div className="text-[10px] uppercase font-bold text-primary">Nombre final del grupo unido</div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="radio" checked={mergeNombreElegido === "target"} onChange={() => setMergeNombreElegido("target")} data-testid="merge-nombre-target" />
                      <span>Usar el nombre de la familia <b>destino</b> (la que voy a elegir abajo)</span>
                    </label>
                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="radio" checked={mergeNombreElegido === "source"} onChange={() => setMergeNombreElegido("source")} data-testid="merge-nombre-source" />
                      <span>Usar el nombre <b>"{familia.familiaNombre}"</b> (este grupo)</span>
                    </label>
                  </div>
                )}

                {/* Búsqueda del dropdown */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
                  <Input
                    value={buscarFamilia}
                    onChange={(e) => setBuscarFamilia(e.target.value)}
                    placeholder="Buscar familia destino..."
                    className="pl-8 h-8 text-sm"
                    data-testid="mover-buscar-familia"
                  />
                </div>

                {/* Lista de familias existentes */}
                <div className="max-h-56 overflow-y-auto space-y-0.5 border border-border rounded-lg p-1">
                  {familiasFiltradas.map((f) => (
                    <button
                      key={f.familia_key}
                      onClick={() => ejecutarMover(f.familia_key, f.nombre_representativo)}
                      disabled={guardando}
                      className="w-full text-left px-2 py-1.5 rounded-md hover:bg-accent text-sm flex items-center justify-between disabled:opacity-50"
                      data-testid={`mover-target-${f.familia_key.substring(0, 20)}`}
                    >
                      <span className="truncate">{f.nombre_representativo}</span>
                      <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                        {f.variantes_count} {f.variantes_count === 1 ? "color" : "colores"}
                      </span>
                    </button>
                  ))}
                  {familiasFiltradas.length === 0 && (
                    <div className="text-xs italic text-muted-foreground px-2 py-2 text-center">
                      {buscarFamilia ? "Ninguna familia coincide con la búsqueda." : "No hay otras familias todavía."}
                    </div>
                  )}
                </div>

                {/* Crear grupo nuevo (solo en modo single o multi, no en merge) */}
                {modo !== "merge" && (
                  <div className="border-t border-border pt-2 space-y-1.5">
                    <div className="text-[10px] uppercase font-bold text-muted-foreground flex items-center gap-1">
                      <Plus className="w-3 h-3" /> Crear familia nueva
                    </div>
                    <div className="flex gap-1">
                      <Input
                        value={nombreGrupoNuevo}
                        onChange={(e) => setNombreGrupoNuevo(e.target.value)}
                        placeholder="Ej: Papel Coreano M12"
                        className="h-8 text-sm"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const nombre = nombreGrupoNuevo.trim();
                            if (!nombre) { toast.error("Escribe el nombre del nuevo grupo"); return; }
                            ejecutarMover(nombre.toUpperCase(), nombre);
                            setNombreGrupoNuevo("");
                          }
                        }}
                        data-testid="mover-nueva-input"
                      />
                      <Button
                        size="sm"
                        onClick={() => {
                          const nombre = nombreGrupoNuevo.trim();
                          if (!nombre) { toast.error("Escribe el nombre del nuevo grupo"); return; }
                          ejecutarMover(nombre.toUpperCase(), nombre);
                          setNombreGrupoNuevo("");
                        }}
                        disabled={guardando || !nombreGrupoNuevo.trim()}
                        data-testid="mover-nueva-btn"
                      >
                        {guardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Crear"}
                      </Button>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-2 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={() => { setMoverAbiertoPara(null); setBuscarFamilia(""); }} disabled={guardando}>
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Sub-modal: eliminar UN color/ítem individual con motivo + ajuste de stock */}
        {eliminarVarianteTarget && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => !eliminando && setEliminarVarianteTarget(null)}>
            <div
              className="bg-card rounded-lg shadow-xl max-w-md w-full p-5 space-y-4"
              onClick={(e) => e.stopPropagation()}
              data-testid="eliminar-variante-modal"
            >
              <div className="flex items-center gap-2 border-b border-border pb-3">
                <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                  <Trash2 className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-semibold text-foreground">Eliminar este ítem</div>
                  <div className="text-xs text-muted-foreground">"{eliminarVarianteTarget.color}" de {familia.familiaNombre}</div>
                </div>
              </div>

              <div className="rounded-md bg-muted/40 border border-border/60 px-3 py-2 text-xs space-y-0.5">
                <div><b>Stock actual:</b> {formatNumber(eliminarVarianteTarget.v.stock_actual || 0)} un</div>
                {(eliminarVarianteTarget.v.stock_reservado || 0) > 0 && (
                  <div className="text-amber-800"><b>Reservado:</b> {formatNumber(eliminarVarianteTarget.v.stock_reservado)} un (pedidos confirmados)</div>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Motivo <span className="text-rose-600">*</span></label>
                <textarea
                  value={eliminarMotivo}
                  onChange={(e) => setEliminarMotivo(e.target.value)}
                  placeholder="Ej: producto dañado, error de catálogo, descontinuado..."
                  className="w-full min-h-[70px] rounded-md border border-border px-3 py-2 text-sm resize-none"
                  autoFocus
                  data-testid="eliminar-variante-motivo"
                />
                <div className="text-[10px] text-muted-foreground">Se registra en el histórico de movimientos y todos los directores/vendedores lo verán en la notificación.</div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Stock final al eliminar</label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={eliminarStockFinal}
                    onChange={(e) => setEliminarStockFinal(e.target.value)}
                    className="h-9 text-sm w-24"
                    data-testid="eliminar-variante-stock"
                  />
                  <div className="text-xs text-muted-foreground">
                    {Number(eliminarStockFinal || 0) === 0
                      ? "→ se dará de baja todo el stock (write-off)"
                      : `→ ajuste de ${Number(eliminarStockFinal) - (eliminarVarianteTarget.v.stock_actual || 0) > 0 ? "+" : ""}${Number(eliminarStockFinal) - (eliminarVarianteTarget.v.stock_actual || 0)} un`}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-border">
                <Button variant="ghost" size="sm" onClick={() => setEliminarVarianteTarget(null)} disabled={eliminando} data-testid="eliminar-variante-cancelar">
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  onClick={confirmarEliminarVariante}
                  disabled={eliminando || !eliminarMotivo.trim()}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="eliminar-variante-confirmar"
                >
                  {eliminando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Eliminar ítem"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Sub-modal: merge proactivo de duplicados (Feb/2026) — detecta y
            fusiona 2+ variantes con el mismo color manteniendo lotes separados.
            El usuario elige cuál es la variante "principal" (master). */}
        {fusionDupTarget && (() => {
          const grupo = fusionDupTarget.grupo;
          const totalStock = grupo.reduce((s, v) => s + Number(v.stock_actual || 0), 0);
          // El "shell" (producto que conserva su identidad) ya fue auto-elegido
          // al abrir el modal: es la variante más antigua para conservar el
          // histórico de pedidos. Pero conceptualmente el usuario sólo ve "lotes
          // fusionados" — FIFO decide el orden de consumo por fecha_recepcion.
          const sorted = [...grupo].sort((a, b) => {
            const ta = a.created_at ? new Date(a.created_at).getTime() : Number.MAX_SAFE_INTEGER;
            const tb = b.created_at ? new Date(b.created_at).getTime() : Number.MAX_SAFE_INTEGER;
            return ta - tb;
          });
          return (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => !fusionGuardando && setFusionDupTarget(null)}>
              <div className="bg-card rounded-lg shadow-2xl max-w-xl w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="fam-fusion-modal">
                <div className="flex items-center gap-2 border-b border-border pb-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Fusionar {grupo.length} variantes duplicadas</div>
                    <div className="text-xs text-muted-foreground">Los lotes se mantienen separados. FIFO consume del más antiguo primero.</div>
                  </div>
                </div>

                <div className="space-y-2">
                  {sorted.map((v, i) => {
                    const stockV = Number(v.stock_actual || 0);
                    const color = v.color || v.nombre?.split(" - ")[1] || "—";
                    const fecha = v.created_at ? new Date(v.created_at).toLocaleDateString("es-CO", { year: "numeric", month: "short", day: "numeric" }) : "—";
                    return (
                      <div
                        key={v.id}
                        className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/20"
                        data-testid={`fam-fusion-opt-${v.id}`}
                      >
                        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold tabular-nums flex-shrink-0">
                          {i + 1}
                        </div>
                        {v.imagen_path ? (
                          <img src={`${API}/catalogo/img/${v.id}?w=120&v=${v.imagen_actualizada_at || 0}`} alt={color} loading="lazy" className="w-12 h-12 rounded-md object-cover border border-border flex-shrink-0" />
                        ) : (
                          <div className="w-12 h-12 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground/40 flex-shrink-0">
                            <ImageIcon className="w-5 h-5" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold truncate">{color}</span>
                            {i === 0 && <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700" title="Lote más antiguo, FIFO lo consume primero">+ Antiguo</span>}
                          </div>
                          <div className="text-[10px] font-mono text-muted-foreground truncate">{v.codigo || v.sku || "—"}</div>
                          <div className="text-[10px] text-muted-foreground/80">Ingreso: {fecha}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-bold tabular-nums">{formatNumber(stockV)}</div>
                          <div className="text-[9px] text-muted-foreground">un</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 space-y-1">
                  <div className="font-semibold flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" /> Resultado tras fusionar:
                  </div>
                  <div className="ml-5">• <span className="font-bold">{grupo.length} variantes</span> se unen en una sola</div>
                  <div className="ml-5">• Stock total: <span className="font-bold tabular-nums">{totalStock} unidades</span></div>
                  <div className="ml-5">• {grupo.length} lotes separados · FIFO consume en orden de antigüedad</div>
                  <div className="ml-5">• Pedidos activos se reasignan automáticamente al color unificado</div>
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={() => setFusionDupTarget(null)} disabled={fusionGuardando} data-testid="fam-fusion-cancelar">
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={fusionarDuplicados}
                    disabled={fusionGuardando}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="fam-fusion-confirmar"
                  >
                    {fusionGuardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Fusionar ${grupo.length} lotes`}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Sub-modal: merge de stock cuando la etiqueta ya existe */}
        {labelMergeTarget && (() => {
          const src = labelMergeTarget.source;
          const tgt = labelMergeTarget.target;
          const srcStock = Number(src.stock_actual || 0);
          const tgtStock = Number(tgt.stock_actual || 0);
          const totalFinal = srcStock + tgtStock;
          return (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => !labelGuardando && setLabelMergeTarget(null)}>
              <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-5 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="label-merge-modal">
                <div className="flex items-center gap-2 border-b border-border pb-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center">
                    <Layers className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Ya existe "{labelMergeTarget.nuevoLabel}"</div>
                    <div className="text-xs text-muted-foreground">Puedes fusionar el stock o cambiar el nombre</div>
                  </div>
                </div>
                <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <span>Este ítem ({src.nombre?.split(" - ").pop() || "actual"})</span>
                    <span className="font-semibold tabular-nums">{srcStock} un</span>
                  </div>
                  <div className="flex items-center justify-between text-primary">
                    <span>Existente "{labelMergeTarget.nuevoLabel}"</span>
                    <span className="font-semibold tabular-nums">{tgtStock} un</span>
                  </div>
                  <div className="border-t border-border/60 pt-2 flex items-center justify-between font-bold">
                    <span>Total si fusionas</span>
                    <span className="tabular-nums text-primary">{totalFinal} un</span>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">
                  El stock de este ítem se sumará al existente y este ítem quedará archivado. Los pedidos anteriores NO se verán afectados.
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                  <Button variant="ghost" size="sm" onClick={() => setLabelMergeTarget(null)} disabled={labelGuardando} data-testid="label-merge-cancelar">
                    Cambiar nombre
                  </Button>
                  <Button size="sm" onClick={confirmarMerge} disabled={labelGuardando} className="bg-primary hover:bg-primary/90" data-testid="label-merge-confirmar">
                    {labelGuardando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Fusionar (${totalFinal} unidades total)`}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </DialogContent>
      {/* Lightbox para ver fotos de las variantes en grande */}
      <ImageLightbox
        src={lightbox?.src}
        alt={lightbox?.alt}
        open={!!lightbox}
        onOpenChange={(v) => { if (!v) setLightbox(null); }}
      />
    </Dialog>
  );
}

function StatBadge({ label, value, tone = "neutral", icon: Icon = null }) {
  const map = {
    primary: "bg-primary/10 text-primary border-primary/30",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-700 border-rose-200",
    muted: "bg-muted text-muted-foreground border-border",
    neutral: "bg-card text-foreground border-border",
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${map[tone] || map.neutral}`}>
      <div className="text-[9px] uppercase font-bold tracking-wider opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-2.5 h-2.5" />} {label}
      </div>
      <div className="text-sm font-semibold tabular-nums mt-0.5 truncate">{value}</div>
    </div>
  );
}
